import { useRef, useState } from 'react';
import type * as React from 'react';
import { useGarden } from '../../garden/GardenContext';
import type { ShapeVertex } from '../../garden/gardenReducer';
import type { Point } from '../../utils/geometry';

interface ShapeVertexHandleProps {
  point: ShapeVertex;
}

function ShapeVertexHandle({ point }: ShapeVertexHandleProps) {
  const { updateShapePoint, removeShapePoint, zoom } = useGarden();
  const [dragPos, setDragPos] = useState<Point | null>(null);
  const startClientRef = useRef<Point>({ x: 0, y: 0 });
  const startPointRef = useRef<Point>({ x: 0, y: 0 });
  const draggingRef = useRef(false);

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    startClientRef.current = { x: event.clientX, y: event.clientY };
    startPointRef.current = { x: point.x, y: point.y };
    draggingRef.current = true;
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    const dx = (event.clientX - startClientRef.current.x) / zoom;
    const dy = (event.clientY - startClientRef.current.y) / zoom;
    setDragPos({ x: startPointRef.current.x + dx, y: startPointRef.current.y + dy });
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    draggingRef.current = false;
    const dx = (event.clientX - startClientRef.current.x) / zoom;
    const dy = (event.clientY - startClientRef.current.y) / zoom;
    updateShapePoint(point.id, startPointRef.current.x + dx, startPointRef.current.y + dy);
    setDragPos(null);
  }

  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    removeShapePoint(point.id);
  }

  const position = dragPos || point;

  return (
    <div
      className="shape-vertex-handle"
      style={{ left: position.x, top: position.y }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      title="Drag to move, double-click to remove"
    />
  );
}

export default ShapeVertexHandle;
